import { audiosService } from '@/services/audios.service';
import { useCallback, useEffect, useState } from 'react';

// ── Hook: obtener URL del formulario de audio ────────────────────────────────

interface UseAudioFormUrlResult {
  data: { formUrl: string; source: 'DB' | 'GENERATED' } | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAudioFormUrl(nit: string, enabled: boolean = true): UseAudioFormUrlResult {
  const [data, setData] = useState<{ formUrl: string; source: 'DB' | 'GENERATED' } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFormUrl = useCallback(async () => {
    if (!enabled || !nit) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await audiosService.getFormUrl(nit);
      if (response.success) {
        setData({ formUrl: response.formUrl, source: response.source });
      } else {
        setError('Error al cargar el formulario de audio');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el formulario de audio');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit]);

  useEffect(() => {
    fetchFormUrl();
  }, [fetchFormUrl]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchFormUrl,
  };
}

// ── Hook: actualizar fecha de grabación de un audio ──────────────────────────

interface UseUpdateAudioFechaResult {
  updateFecha: (audioId: number, fecha: Date) => Promise<void>;
  isUpdating: boolean;
  error: string | null;
}

export function useUpdateAudioFecha(nit: string, onSuccess?: () => void): UseUpdateAudioFechaResult {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFecha = useCallback(
    async (audioId: number, fecha: Date) => {
      setIsUpdating(true);
      setError(null);
      try {
        await audiosService.updateFechaGrabacion(nit, audioId, fecha);
        onSuccess?.();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error actualizando fecha de audio';
        setError(msg);
        throw new Error(msg);
      } finally {
        setIsUpdating(false);
      }
    },
    [nit, onSuccess]
  );

  return { updateFecha, isUpdating, error };
}

// ── Hook: eliminar un audio ──────────────────────────────────────────────────

interface UseDeleteAudioResult {
  deleteAudio: (audioId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export function useDeleteAudio(nit: string, onSuccess?: () => void): UseDeleteAudioResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAudio = useCallback(
    async (audioId: number) => {
      setIsDeleting(true);
      setError(null);
      try {
        await audiosService.deleteAudio(nit, audioId);
        onSuccess?.();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al eliminar el audio';
        setError(msg);
        throw new Error(msg);
      } finally {
        setIsDeleting(false);
      }
    },
    [nit, onSuccess]
  );

  return { deleteAudio, isDeleting, error };
}

// ── Hook: solicitar acceso al audio ──────────────────────────────────────────

interface UseRequestAudioAccessResult {
  requestAccess: (audioId: number, email?: string) => Promise<void>;
  isRequesting: boolean;
  error: string | null;
  successMessage: string | null;
  clearStatus: () => void;
}

export function useRequestAudioAccess(nit: string): UseRequestAudioAccessResult {
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const requestAccess = useCallback(
    async (audioId: number, email?: string) => {
      setIsRequesting(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const result = await audiosService.requestAudioAccess(nit, audioId, email);
        setSuccessMessage(result.message);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al solicitar acceso al audio';
        setError(msg);
        throw new Error(msg);
      } finally {
        setIsRequesting(false);
      }
    },
    [nit]
  );

  const clearStatus = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  return { requestAccess, isRequesting, error, successMessage, clearStatus };
}

// ── Hook: solicitar acceso al formulario ─────────────────────────────────────

interface UseRequestFormAccessResult {
  requestAccess: (email?: string) => Promise<void>;
  isRequesting: boolean;
  error: string | null;
  successMessage: string | null;
  clearStatus: () => void;
}

export function useRequestFormAccess(nit: string): UseRequestFormAccessResult {
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const requestAccess = useCallback(
    async (email?: string) => {
      setIsRequesting(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const result = await audiosService.requestFormAccess(nit, email);
        setSuccessMessage(result.message);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al solicitar acceso al formulario';
        setError(msg);
        throw new Error(msg);
      } finally {
        setIsRequesting(false);
      }
    },
    [nit]
  );

  const clearStatus = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  return { requestAccess, isRequesting, error, successMessage, clearStatus };
}
